import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Fieldset indices the user has manually clicked during this page session.
   * @type {Set<number>}
   */
  #touchedFieldsetIndices = new Set();

  /**
   * Fieldset indices that were pre-selected via ?variant= URL param.
   * Once the user clicks a fieldset it moves into #touchedFieldsetIndices.
   * @type {Set<number>}
   */
  #urlPreselectedIndices = new Set();

  /** @type {((e: Event) => void) | null} */
  #cartClickBlocker = null;

  /** @type {number} */
  #lastChangedFieldsetIdx = -1;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    this.#injectStyles();

    if (!this.closest('product-card') && !this.closest('quick-add-dialog')) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasVariantParam = urlParams.has('variant');

      if (hasVariantParam) {
        fieldsets.forEach((fieldset) => {
          const idx = Number.parseInt(fieldset.dataset.fieldsetIndex ?? '');
          if (!Number.isNaN(idx)) {
            this.#urlPreselectedIndices.add(idx);
          }
        });
        requestAnimationFrame(() => this.#syncAddToCartButton());
      } else {
        this.#clearAllSelections();
        requestAnimationFrame(() => {
          this.#syncAddToCartButton();
          setTimeout(() => this.#syncAddToCartButton(), 0);
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#removeCartClickBlocker();
  }

  get #isProductPage() {
    return !this.closest('product-card') && !this.closest('quick-add-dialog');
  }

  /**
   * Injects CSS for the select-a-size button state.
   * Uses data-needs-size attribute to drive styles so Horizon re-renders
   * never wipe our changes.
   */
  #injectStyles() {
    if (document.getElementById('vp-select-size-styles')) return;
    const labelSelectSize = this.dataset.labelSelectSize || 'Select a Size';
    const labelSoldOut = this.dataset.labelSoldOut || 'Sold Out';
    const labelAddToCart = this.dataset.labelAddToCart || 'Add to cart';
    const style = document.createElement('style');
    style.id = 'vp-select-size-styles';
    style.textContent = `
      button[name="add"][data-needs-size="true"] .add-to-cart-text__content,
      button[name="add"][data-sold-out="true"] .add-to-cart-text__content,
      button[name="add"][data-updating="true"] .add-to-cart-text__content {
        visibility: hidden;
      }
      button[name="add"][data-needs-size="true"]::after,
      button[name="add"][data-sold-out="true"]::after,
      button[name="add"][data-updating="true"]::after {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: inherit;
        font-size: inherit;
        font-weight: inherit;
        color: inherit;
        pointer-events: none;
      }
      button[name="add"][data-needs-size="true"]::after { content: "${labelSelectSize}"; }
      button[name="add"][data-sold-out="true"]::after { content: "${labelSoldOut}"; }
      button[name="add"][data-updating="true"]::after { content: "${labelAddToCart}"; }
      button[name="add"][data-needs-size="true"] {
        opacity: 0.6;
        cursor: not-allowed;
      }
      button[name="add"][data-needs-size="true"] *,
      button[name="add"][data-sold-out="true"] * {
        pointer-events: none;
      }
      button[name="add"][data-sold-out="true"] {
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Attaches a click blocker to the add-to-cart component and button
   * to prevent the cart from being opened when size is not selected.
   */
  #attachCartClickBlocker() {
    this.#removeCartClickBlocker(); // remove any existing one first

    const btn = this.#findAddToCartBtn();
    if (!btn) return;

    // Block on the add-to-cart-component (parent) so it fires before Horizon's handler
    const addToCartComponent = btn.closest('add-to-cart-component') ?? btn;

    this.#cartClickBlocker = (e) => {
      const currentBtn = this.#findAddToCartBtn();
      if (currentBtn?.hasAttribute('data-needs-size')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    addToCartComponent.addEventListener('click', this.#cartClickBlocker, true); // capture phase
    this.#cartClickBlockerTarget = addToCartComponent;
  }

  /** @type {Element | null} */
  #cartClickBlockerTarget = null;

  #removeCartClickBlocker() {
    if (this.#cartClickBlocker && this.#cartClickBlockerTarget) {
      this.#cartClickBlockerTarget.removeEventListener('click', this.#cartClickBlocker, true);
      this.#cartClickBlocker = null;
      this.#cartClickBlockerTarget = null;
    }
  }

  /**
   * A fieldset is protected (should not be cleared) if the user has touched
   * it OR it was pre-selected via the URL param.
   * @param {number} idx
   * @returns {boolean}
   */
  #isProtected(idx) {
    return this.#touchedFieldsetIndices.has(idx) || this.#urlPreselectedIndices.has(idx);
  }

  /**
   * Returns true only if every fieldset has a checked input in the live DOM,
   * excluding fieldsets that are currently hidden (waiting for fetch).
   * @returns {boolean}
   */
  #allFieldsetsSelected() {
    const fieldsets = this.querySelectorAll('fieldset[data-fieldset-index]');
    if (fieldsets.length === 0) return false;

    return Array.from(fieldsets).every((fieldset) => {
      if (fieldset instanceof HTMLElement && fieldset.style.visibility === 'hidden') return false;
      return fieldset.querySelector('input:checked') !== null;
    });
  }

  /**
   * Syncs Add to Cart button state based on actual checked inputs in the DOM.
   */
  #syncAddToCartButton() {
    const btn = this.#findAddToCartBtn();
    if (!(btn instanceof HTMLButtonElement)) return;

    const labelSelectSize = this.dataset.labelSelectSize || 'Select a Size';
    const labelSoldOut = this.dataset.labelSoldOut || 'Sold Out';
    const labelAddToCart = this.dataset.labelAddToCart || 'Add to cart';

    if (this.#allFieldsetsSelected()) {
      const checkedInputs = Array.from(this.querySelectorAll('fieldset input:checked'));
      const lastIdx = this.#lastChangedFieldsetIdx;
      const isAvailable = checkedInputs.every((input) => {
        if (!(input instanceof HTMLInputElement)) return true;
        if (lastIdx !== -1) {
          const idx = Number.parseInt(input.dataset.fieldsetIndex ?? '');
          if (idx !== lastIdx) return true;
        }
        return input.dataset.optionAvailable !== 'false';
      });
      btn.removeAttribute('data-needs-size');
      this.#removeCartClickBlocker();
      if (isAvailable) {
        btn.removeAttribute('data-sold-out');
        if (lastIdx !== -1) {
          btn.setAttribute('data-updating', 'true');
          btn.setAttribute('aria-label', labelAddToCart);
          btn.disabled = true;
        } else {
          btn.removeAttribute('data-updating');
          btn.removeAttribute('aria-label');
          btn.disabled = false;
        }
      } else {
        btn.setAttribute('data-sold-out', 'true');
        btn.setAttribute('aria-label', labelSoldOut);
        btn.removeAttribute('data-updating');
        btn.disabled = true;
      }
    } else {
      btn.disabled = true;
      btn.setAttribute('data-needs-size', 'true');
      btn.setAttribute('aria-label', labelSelectSize);
      btn.removeAttribute('data-sold-out');
      btn.removeAttribute('data-updating');
      this.#attachCartClickBlocker();
    }
  }

  /**
   * Clears ALL variant selections. Used on initial load with no ?variant= param.
   */
  #clearAllSelections() {
    this.querySelectorAll('fieldset input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.dataset.currentChecked = 'false';
        input.dataset.previousChecked = 'false';
      }
    });

    this.querySelectorAll('.variant-option__button-label__pill').forEach((pill) => {
      if (pill instanceof HTMLElement) pill.style.transform = 'translateX(200%)';
    });

    this.querySelectorAll('select').forEach((select) => {
      if (select instanceof HTMLSelectElement) select.selectedIndex = -1;
    });

    this.#checkedIndices = [];
  }

  /**
   * Clears unprotected fieldsets on the live DOM after morph.
   */
  #clearUntouchedLive() {
    this.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLElement)) return;
      const idx = Number.parseInt(fieldset.dataset.fieldsetIndex ?? '');
      if (Number.isNaN(idx) || this.#isProtected(idx)) return;

      fieldset.querySelectorAll('input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.dataset.currentChecked = 'false';
          input.dataset.previousChecked = 'false';
        }
      });

      fieldset.querySelectorAll('.variant-option__button-label__pill').forEach((pill) => {
        if (pill instanceof HTMLElement) pill.style.transform = 'translateX(200%)';
      });

      this.#checkedIndices[idx] = [];
    });
  }

  /**
   * Mutates incoming HTML before morph — clears unprotected fieldsets so
   * they never flash as selected in the DOM.
   * @param {Element} source
   */
  #preCleanHtml(source) {
    source.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLElement)) return;
      const idx = Number.parseInt(fieldset.dataset.fieldsetIndex ?? '');
      if (Number.isNaN(idx) || this.#isProtected(idx)) return;

      fieldset.querySelectorAll('input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.removeAttribute('checked');
          input.dataset.currentChecked = 'false';
          input.dataset.previousChecked = 'false';
        }
      });

      fieldset.querySelectorAll('.variant-option__button-label__pill').forEach((pill) => {
        if (pill instanceof HTMLElement) pill.style.transform = 'translateX(200%)';
      });
    });
  }

  /**
   * Finds the Add to Cart button by searching up the DOM tree.
   * @returns {HTMLButtonElement | null}
   */
  #findAddToCartBtn() {
    const form = this.closest('form');
    if (form) {
      const btn = form.querySelector('button[name="add"]');
      if (btn instanceof HTMLButtonElement) return btn;
    }

    const productDetails = this.closest('.product-details, product-details, [data-product-details]');
    if (productDetails) {
      const btn = productDetails.querySelector('button[name="add"]');
      if (btn instanceof HTMLButtonElement) return btn;
    }

    const section = this.closest('section, main');
    if (section) {
      const btn = section.querySelector('button[name="add"]');
      if (btn instanceof HTMLButtonElement) return btn;
    }

    return null;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target instanceof HTMLInputElement) {
      const idx = Number.parseInt(event.target.dataset.fieldsetIndex ?? '');
      if (!Number.isNaN(idx)) {
        this.#lastChangedFieldsetIdx = idx;
        this.#urlPreselectedIndices.delete(idx);
        this.#touchedFieldsetIndices.add(idx);
        this.#lastChangedFieldsetIdx = idx;
      }
    }

    this.querySelectorAll('.variant-option__button-label__pill').forEach((pill) => {
      if (pill instanceof HTMLElement) pill.style.transform = '';
    });

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];

        if (!this.#checkedIndices[fieldsetIndex]) {
          this.#checkedIndices[fieldsetIndex] = [];
        }

        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (this.#isProductPage) {
      this.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
        if (!(fieldset instanceof HTMLElement)) return;
        const idx = Number.parseInt(fieldset.dataset.fieldsetIndex ?? '');
        if (Number.isNaN(idx) || this.#isProtected(idx)) return;

        fieldset.style.visibility = 'hidden';
        fieldset.querySelectorAll('input').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.checked = false;
            input.dataset.currentChecked = 'false';
            input.dataset.previousChecked = 'false';
          }
        });
      });

      this.#syncAddToCartButton();
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (this.#isProductPage) {
          this.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
            if (fieldset instanceof HTMLElement) fieldset.style.visibility = '';
          });
        }
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          this.#lastChangedFieldsetIdx = -1;
          this.#syncAddToCartButton();
        }
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    if (this.#isProductPage) {
      this.#preCleanHtml(newVariantPickerSource);
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });

    this.updateVariantPickerCss();

    if (this.#isProductPage) {
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
      this.#radios = fieldsets.map((fieldset) => Array.from(fieldset?.querySelectorAll('input') ?? []));

      this.#clearUntouchedLive();

      this.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
        if (fieldset instanceof HTMLElement) fieldset.style.visibility = '';
      });

      this.#lastChangedFieldsetIdx = -1;
      this.#syncAddToCartButton();
    }

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) throw new Error('No option value ID found');

    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}